import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { sans, mono, green, greenLight, useTheme } from '../../tokens';

const inputStyle = (t) => ({
  fontFamily: sans,
  fontSize: 12,
  padding: '8px 12px',
  border: '1px solid ' + t.border,
  borderRadius: 6,
  background: t.inputBg,
  color: t.textPrimary,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
});

const labelStyle = (t) => ({
  fontSize: 11,
  fontWeight: 600,
  color: t.textSecondary,
  fontFamily: sans,
  marginBottom: 6,
  display: 'block',
});

const sectionTitleStyle = (t) => ({
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: t.textMuted,
  fontFamily: sans,
  marginBottom: 16,
  paddingBottom: 8,
  borderBottom: '1px solid ' + t.border,
});

const toggleStyle = (enabled, t) => ({
  width: 36,
  height: 20,
  borderRadius: 10,
  background: enabled ? green : t.border,
  position: 'relative',
  cursor: 'pointer',
  transition: 'background 0.15s',
  flexShrink: 0,
});

const toggleKnob = (enabled) => ({
  width: 16,
  height: 16,
  borderRadius: '50%',
  background: '#fff',
  position: 'absolute',
  top: 2,
  left: enabled ? 18 : 2,
  transition: 'left 0.15s',
  boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
});

const MODULES_EXPORT = [
  { key: 'close', label: 'Close & Review' },
  { key: 'fpa', label: 'FP&A / Budget' },
  { key: 'equity', label: 'Equity' },
  { key: 'investor', label: 'Investor Reports' },
  { key: 'dashboard', label: 'Dashboard' },
];

const FORMATS = ['pdf', 'xlsx', 'csv'];

const INITIAL_LISTS = [
  { id: 1, name: 'Board Package', recipients: 5, format: 'PDF', schedule: 'Quarterly' },
  { id: 2, name: 'Monthly Close Report', recipients: 3, format: 'PDF + XLSX', schedule: 'Monthly' },
  { id: 3, name: 'Investor Update', recipients: 12, format: 'PDF', schedule: 'Quarterly' },
];

export default function ReportsSettings() {
  const t = useTheme();
  const savedReports = JSON.parse(localStorage.getItem('finkloz_reports_settings') || 'null');
  const [template, setTemplate] = useState(savedReports?.template || {
    logoPosition: 'left',
    confidentialityNotice: 'This document is confidential and intended solely for the use of the individual or entity to whom it is addressed.',
    watermarkConfidential: true,
    watermarkDraft: true,
    watermarkUnaudited: 'footer',
  });
  const [font, setFont] = useState(savedReports?.font || 'Inter');
  const [exportFormats, setExportFormats] = useState(
    savedReports?.exportFormats || Object.fromEntries(MODULES_EXPORT.map(m => [m.key, { pdf: true, xlsx: true, csv: false }]))
  );
  const [lists, setLists] = useState(savedReports?.lists || INITIAL_LISTS);
  const [showAddList, setShowAddList] = useState(false);
  const [newList, setNewList] = useState({ name: '', recipients: '', format: 'PDF', schedule: 'Monthly' });
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);

  const markDirty = () => setDirty(true);

  const handleSave = () => {
    localStorage.setItem('finkloz_reports_settings', JSON.stringify({
      template, font, exportFormats, lists,
    }));
    setSaved(true);
    setDirty(false);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleAddList = () => {
    if (!newList.name) return;
    setLists(prev => [...prev, { id: Date.now(), ...newList, recipients: parseInt(newList.recipients) || 0 }]);
    setNewList({ name: '', recipients: '', format: 'PDF', schedule: 'Monthly' });
    setShowAddList(false);
    markDirty();
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: sans, margin: '0 0 4px' }}>
        Reports & Exports
      </h2>
      <p style={{ fontSize: 12, color: t.textMuted, fontFamily: sans, margin: '0 0 24px' }}>
        PDF template settings, export formats, and distribution lists.
      </p>

      {/* PDF Template */}
      <div style={sectionTitleStyle(t)}>PDF Template</div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle(t)}>Logo Position</label>
        <div style={{ display: 'flex', gap: 16 }}>
          {['left', 'center', 'right'].map(pos => (
            <label key={pos} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 12, fontFamily: sans, color: t.textPrimary, cursor: 'pointer',
            }}>
              <input
                type="radio"
                name="logoPosition"
                value={pos}
                checked={template.logoPosition === pos}
                onChange={() => { setTemplate(p => ({ ...p, logoPosition: pos })); markDirty(); }}
                style={{ accentColor: green }}
              />
              {pos.charAt(0).toUpperCase() + pos.slice(1)}
            </label>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle(t)}>Confidentiality Notice</label>
        <textarea
          value={template.confidentialityNotice}
          onChange={e => { setTemplate(p => ({ ...p, confidentialityNotice: e.target.value })); markDirty(); }}
          rows={3}
          style={{ ...inputStyle(t), resize: 'vertical' }}
        />
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, color: t.textSecondary, fontFamily: sans, marginBottom: 12 }}>
        Watermarks
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            onClick={() => { setTemplate(p => ({ ...p, watermarkConfidential: !p.watermarkConfidential })); markDirty(); }}
            style={toggleStyle(template.watermarkConfidential, t)}
          >
            <div style={toggleKnob(template.watermarkConfidential)} />
          </div>
          <span style={{ fontSize: 12, color: t.textPrimary, fontFamily: sans }}>CONFIDENTIAL watermark</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            onClick={() => { setTemplate(p => ({ ...p, watermarkDraft: !p.watermarkDraft })); markDirty(); }}
            style={toggleStyle(template.watermarkDraft, t)}
          >
            <div style={toggleKnob(template.watermarkDraft)} />
          </div>
          <span style={{ fontSize: 12, color: t.textPrimary, fontFamily: sans }}>DRAFT auto-applied to unfinalized reports</span>
        </div>
      </div>

      <div style={{ marginBottom: 32 }}>
        <label style={labelStyle(t)}>UNAUDITED Placement</label>
        <div style={{ display: 'flex', gap: 16 }}>
          {[
            { value: 'header', label: 'Header' },
            { value: 'footer', label: 'Footer' },
            { value: 'watermark', label: 'Watermark' },
          ].map(opt => (
            <label key={opt.value} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 12, fontFamily: sans, color: t.textPrimary, cursor: 'pointer',
            }}>
              <input
                type="radio"
                name="unaudited"
                value={opt.value}
                checked={template.watermarkUnaudited === opt.value}
                onChange={() => { setTemplate(p => ({ ...p, watermarkUnaudited: opt.value })); markDirty(); }}
                style={{ accentColor: green }}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      {/* Font */}
      <div style={{ marginBottom: 32 }}>
        <label style={labelStyle(t)}>Report Font</label>
        <select
          value={font}
          onChange={e => { setFont(e.target.value); markDirty(); }}
          style={{ ...inputStyle(t), maxWidth: 300 }}
        >
          <option value="Inter">Inter</option>
          <option value="Helvetica Neue">Helvetica Neue</option>
          <option value="Arial">Arial</option>
        </select>
      </div>

      {/* Export Formats */}
      <div style={sectionTitleStyle(t)}>Export Formats</div>

      <div style={{
        border: '1px solid ' + t.border,
        borderRadius: 8,
        overflow: 'hidden',
        marginBottom: 32,
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sans, fontSize: 12 }}>
          <thead>
            <tr style={{ background: t.bgSurface }}>
              <th style={{
                padding: '10px 14px', textAlign: 'left',
                fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: 0.5, color: t.textMuted,
                borderBottom: '1px solid ' + t.border,
              }}>Module</th>
              {FORMATS.map(f => (
                <th key={f} style={{
                  padding: '10px 14px', textAlign: 'center',
                  fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: 0.5, color: t.textMuted,
                  borderBottom: '1px solid ' + t.border,
                }}>{f.toUpperCase()}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MODULES_EXPORT.map(mod => (
              <tr key={mod.key} style={{ borderBottom: '1px solid ' + t.borderLight }}>
                <td style={{ padding: '10px 14px', color: t.textPrimary, fontWeight: 500 }}>
                  {mod.label}
                </td>
                {FORMATS.map(f => (
                  <td key={f} style={{ padding: '10px 14px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <div
                        onClick={() => {
                          setExportFormats(prev => ({
                            ...prev,
                            [mod.key]: { ...prev[mod.key], [f]: !prev[mod.key][f] },
                          }));
                          markDirty();
                        }}
                        style={toggleStyle(exportFormats[mod.key]?.[f], t)}
                      >
                        <div style={toggleKnob(exportFormats[mod.key]?.[f])} />
                      </div>
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Distribution Lists */}
      <div style={sectionTitleStyle(t)}>Distribution Lists</div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button
          onClick={() => setShowAddList(!showAddList)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', border: 'none', borderRadius: 6,
            background: green, color: '#fff', fontSize: 12, fontWeight: 600,
            fontFamily: sans, cursor: 'pointer',
          }}
        >
          <Plus size={14} />
          Add List
        </button>
      </div>

      {showAddList && (
        <div style={{
          background: t.bgSurface, border: '1px solid ' + t.border,
          borderRadius: 8, padding: 16, marginBottom: 16,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, fontFamily: sans, marginBottom: 12 }}>
            Add Distribution List
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 120px 120px auto', gap: 12, alignItems: 'end' }}>
            <div>
              <label style={labelStyle(t)}>List Name *</label>
              <input
                type="text"
                value={newList.name}
                onChange={e => setNewList(p => ({ ...p, name: e.target.value }))}
                style={inputStyle(t)}
              />
            </div>
            <div>
              <label style={labelStyle(t)}>Recipients</label>
              <input
                type="number"
                value={newList.recipients}
                onChange={e => setNewList(p => ({ ...p, recipients: e.target.value }))}
                style={inputStyle(t)}
              />
            </div>
            <div>
              <label style={labelStyle(t)}>Format</label>
              <select
                value={newList.format}
                onChange={e => setNewList(p => ({ ...p, format: e.target.value }))}
                style={inputStyle(t)}
              >
                <option value="PDF">PDF</option>
                <option value="XLSX">XLSX</option>
                <option value="PDF + XLSX">PDF + XLSX</option>
              </select>
            </div>
            <div>
              <label style={labelStyle(t)}>Schedule</label>
              <select
                value={newList.schedule}
                onChange={e => setNewList(p => ({ ...p, schedule: e.target.value }))}
                style={inputStyle(t)}
              >
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Annually">Annually</option>
                <option value="On demand">On demand</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleAddList} style={{
                padding: '8px 14px', border: 'none', borderRadius: 6,
                background: green, color: '#fff', fontSize: 12, fontWeight: 600,
                fontFamily: sans, cursor: 'pointer',
              }}>
                Add
              </button>
              <button onClick={() => setShowAddList(false)} style={{
                padding: '8px 10px', border: '1px solid ' + t.border, borderRadius: 6,
                background: 'transparent', color: t.textSecondary, fontSize: 12,
                fontFamily: sans, cursor: 'pointer',
              }}>
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{
        border: '1px solid ' + t.border,
        borderRadius: 8,
        overflow: 'hidden',
        marginBottom: 32,
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sans, fontSize: 12 }}>
          <thead>
            <tr style={{ background: t.bgSurface }}>
              {['List Name', 'Recipients', 'Format', 'Schedule'].map(h => (
                <th key={h} style={{
                  padding: '10px 14px', textAlign: 'left',
                  fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: 0.5, color: t.textMuted,
                  borderBottom: '1px solid ' + t.border,
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lists.map(l => (
              <tr key={l.id} style={{ borderBottom: '1px solid ' + t.borderLight }}>
                <td style={{ padding: '10px 14px', color: t.textPrimary, fontWeight: 500 }}>
                  {l.name}
                </td>
                <td style={{ padding: '10px 14px', color: t.textSecondary, fontFamily: mono, fontSize: 11 }}>
                  {l.recipients}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{
                    padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                    background: t.pillBg, color: t.textSecondary,
                  }}>
                    {l.format}
                  </span>
                </td>
                <td style={{ padding: '10px 14px', color: t.textSecondary }}>
                  {l.schedule}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Save */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={!dirty}
          style={{
            padding: '10px 24px',
            border: 'none',
            borderRadius: 6,
            background: dirty ? green : t.border,
            color: dirty ? '#fff' : t.textMuted,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: sans,
            cursor: dirty ? 'pointer' : 'default',
            transition: 'all 0.15s',
          }}
        >
          Save
        </button>
        {saved && (
          <span style={{ fontSize: 12, color: green, fontFamily: sans, fontWeight: 500 }}>
            Settings saved
          </span>
        )}
      </div>
    </div>
  );
}
